import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Masked } from "work/masked-fields";
import {
  proposalDetailsColumnGap,
  proposalDetailsControlMinimumWidth,
  proposalDetailsGridTemplateColumns,
} from "work/proposals/_views/_shared/proposal-details-layout";
import { paneIsCompact, workPaneProseInset } from "work/_views/work-pane-layout";
import Proposal from "work/proposals/proposal";

const ProposalContainer = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    gap: theme.spacing(3),
    gridTemplateColumns: "1fr",
  },
  // This tab is all fields and no table, so the whole of it is prose — see `workPaneProseInset`.
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  boxSizing: "border-box",
  columnGap: proposalDetailsColumnGap,
  display: "grid",
  /**
   * `auto auto` seated two 400px columns whatever the pane had, so on a 584px pane the dates column
   * was laid out past the pane's right edge and the comments drawer hid 110px of every date field
   * (#4964). The draft and active Details tabs already choose their column count from the pane; this
   * is the same rule, which is why it lives in one place.
   */
  gridTemplateColumns: proposalDetailsGridTemplateColumns(theme.spacing(50)),
  height: "100%",
  justifyContent: "start",
  paddingTop: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
}));
const GeneralSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
}));
const NameTextField = styled(TextField)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
}));
const DescriptionTextField = styled(TextField)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
}));
const DateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  width: "100%",
}));

type DetailsTabProps = {
  proposal: Proposal;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const { proposal } = props;

  const individualUserIds = useMemo(() => {
    const ids: Guid[] = [];
    if (Masked.isPresentNotMasked(proposal.clientLeader)) {
      ids.push(proposal.clientLeader.userId);
    }
    return ids;
  }, [proposal.clientLeader]);

  const individualsByUserIds = useSelector(getIndividualsByUserIds(individualUserIds));

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(individualUserIds.map(id => id.value)) },
    { skip: !individualUserIds.length }
  );

  const clientName = Masked.isMasked(proposal.clientLeader)
    ? "Insufficient Permissions to View Client"
    : (individualsByUserIds?.find((individual) =>
        individual.userId.isEqualTo((proposal.clientLeader as EntityMember)?.userId),
      )?.fullName ?? "");

  return (
    <ProposalContainer>
      <GeneralSection>
        <ClientTextField
          disabled={true}
          label={proposal.clientLeader ? "Client Representative" : "Select Client Representative"}
          error={false}
          required
          value={clientName}
        />
        <NameTextField
          label="Proposal Name"
          required={true}
          disabled={true}
          value={proposal.name?.value ?? ""}
          error={false}
        />
        <DescriptionTextField
          label="Description"
          required={true}
          multiline={true}
          rows={3}
          disabled={true}
          value={proposal.description?.value ?? ""}
          error={false}
        />
      </GeneralSection>
      <DateSection>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.responseDueBy?.asMoment ?? null}
            label="Response Due Date"
          />
        </DateContainer>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.startDate?.asMoment}
            label={"Start Date"}
          />
        </DateContainer>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.endDate?.asMoment}
            label="End Date"
          />
        </DateContainer>
      </DateSection>
    </ProposalContainer>
  );
}
